import type { ApparaatStaat, MenuRegel, Scherm, Toestel } from './types';

/**
 * De menustructuur van een Verifone V400M met de CCV-app (softwareversie
 * 15.0.07), overgenomen uit een opname van een echt apparaat.
 *
 * ────────────────────────────────────────────────────────────────────────
 * GEEN WACHTWOORDEN. Op de echte automaat zit er een pincode voor het
 * MANAGER MENU en het SERVICE MENU. Die schermen staan hier bewust niet in:
 * de emulator is er om de wég te wijzen, niet om toegang te verlenen. Wie de
 * code nodig heeft heeft hem al; wie hem niet heeft, hoort hem hier niet te
 * vinden. Het type MenuRegel kent daarom geen pin-veld — zie types.ts.
 *
 * VOORBEELDGEGEVENS. Serienummer, IP-adressen en de gevonden wifi-netwerken
 * zijn VERZONNEN. In de opname stonden echte waarden van een klantlocatie, en
 * die horen niet op een publieke pagina. De menustructuur en de schermteksten
 * zijn wél letterlijk overgenomen — dat is juist wat gereproduceerd moet
 * worden.
 *
 * SAMENGEVOEGDE SCHERMEN. De opname laat een paar schermen twee keer zien,
 * bereikbaar via twee routes (wifi-status via DISPLAY INFO én via
 * COMMUNICATIE; de mobiele-netwerkenlijst idem). Dat is op het apparaat
 * hetzelfde scherm, dus het staat hier ook één keer, met twee verwijzingen
 * ernaartoe. De boom en het kruimelpad kunnen daar prima mee overweg.
 * ────────────────────────────────────────────────────────────────────────
 */

const wifiSub = (s: ApparaatStaat): string => (s.wifiVerbonden ? 'VERBONDEN' : 'GEEN VERBINDING');
const wifiLines = (s: ApparaatStaat): readonly string[] =>
  s.wifiVerbonden ? ['VERBONDEN'] : ['GEEN VERBINDING'];
const ipSub = (s: ApparaatStaat): string => s.ipModus;

/** De regels van FUNCTIES ▸ TERMINAL. Manager en service delen deze lijst op
 *  één regel na: het servicemenu splitst CONTACT TMS nog uit in drie soorten
 *  oproepen. Daarom hier één keer, met de afwijkende regel als argument. */
const functieRegels = <T extends string>(tms: T) =>
  [
    { label: 'ACTIVEER TERMINAL', to: 'act_term' },
    { label: 'DAGOPENING', to: 'dagopening' },
    { label: 'CONTACT TMS', to: tms },
    { label: 'CONT. TMS VANNACHT', to: 'tms_nacht' },
    { label: 'VERSTUUR LOGGING', to: 'logging' },
    { label: 'OPNIEUW STARTEN', to: 'reboot' },
    { label: 'UITZETTEN', to: 'shutdown' },
  ] as const satisfies readonly MenuRegel[];

const RUW = {
  /* ── opstartscherm ───────────────────────────────────────────────────── */
  /* Het CCV-logo over het volle scherm, dat na 1,8 s vanzelf het hoofdmenu
     opent. Kort genoeg om niet in de weg te zitten, lang genoeg om te zien
     dat je naar een apparaat kijkt dat opstart. Doorklikken kan altijd, en
     met de schakelaar "wachtschermen automatisch doorlopen" uit wacht hij
     netjes op jou (WCAG 2.2.1). */
  start: { type: 'splash', title: '', ms: 1800, next: 'main' },

  /* ── hoofdmenu ───────────────────────────────────────────────────────── */
  main: {
    type: 'menu',
    title: 'HOOFD MENU',
    items: [
      { label: 'DISPLAY INFO', to: 'displayinfo' },
      { label: 'MANAGER MENU', to: 'managermenu' },
      { label: 'SERVICE MENU', to: 'servicemenu' },
      { label: 'KOPIE BON', to: 'kopiebon' },
      { label: 'PAPER FEED', to: 'paperfeed' },
    ],
  },
  kopiebon: { type: 'msg', title: 'KOPIE BON', lines: ['GEEN TRANSACTIE', 'BESCHIKBAAR'] },
  paperfeed: { type: 'msg', title: 'PAPER FEED', lines: ['PAPIER DOORVOEREN'] },

  /* ── DISPLAY INFO ────────────────────────────────────────────────────── */
  displayinfo: {
    type: 'menu',
    title: 'DISPLAY INFO',
    items: [
      { label: 'CUSTOMER SERVICE', to: 'di_cs' },
      { label: 'TERMINAL', to: 'di_term' },
      { label: 'WIFI', sub: wifiSub, to: 'wifi_status' },
      { label: 'MOBIELE NETWERKEN', sub: 'GEEN VERBINDING', to: 'mobiel' },
      { label: 'USB GADGET MODE', to: 'di_usb' },
      { label: 'HW INFO', to: 'di_hw' },
      { label: 'UPDATE STATUS', to: 'di_upd' },
    ],
  },
  di_cs: { type: 'info', title: 'CUSTOMER SERVICE', rows: [['TELEFOONNUMMER:', '088 - 000 00 00']] },
  di_term: {
    type: 'info',
    title: 'TERMINAL',
    right: 'V400MNL',
    rows: [
      ['TMS TID:', '00000000'],
      ['CONF:', 'NL-CCV'],
      ['CONF VERS:', '15.0.07.28978'],
      ['SW VERS:', '15.0.07.2897'],
      ['PP VERS:', '14.10.10.2897'],
      ['', '11/09/2026   12:07:04'],
    ],
  },
  di_usb: { type: 'info', title: 'USB GADGET MODE', rows: [['MODE:', 'SERIAL']] },
  di_hw: { type: 'info', title: 'HW INFO', right: '000-000-000', rows: [['S/N:', '000-000-000']] },
  di_upd: {
    type: 'info',
    title: 'UPDATE STATUS',
    rows: [
      ['INSTALLATIE:', 'GEEN'],
      ['ACTUEEL:', 'JA'],
      ['VERSIE:', '15.0.07'],
    ],
  },

  /* ── MANAGER MENU ────────────────────────────────────────────────────── */
  managermenu: {
    type: 'menu',
    title: 'MANAGER MENU',
    items: [
      { label: 'RAPPORTEN', to: 'mgr_rap' },
      { label: 'FUNCTIES', to: 'mgr_fun' },
      { label: 'CONFIGUREREN', to: 'mgr_cfg' },
      { label: 'DIAGNOSE', to: 'mgr_diag' },
    ],
  },

  mgr_rap: { type: 'menu', title: 'RAPPORTEN', items: [{ label: 'TERMINAL', to: 'mgr_rap_term' }] },
  mgr_rap_term: {
    type: 'menu',
    title: 'TERMINAL',
    items: [
      { label: 'FINANCIEEL', to: 'mgr_rap_fin' },
      { label: 'PARAMETERS', to: 'mgr_rap_par' },
    ],
  },
  mgr_rap_fin: { type: 'msg', title: 'TERMINAL', lines: ['GEEN TOTALEN', 'BESCHIKBAAR'] },
  mgr_rap_par: {
    type: 'menu',
    title: 'PARAMETERS',
    items: [
      { label: 'ALGEMEEN', to: 'print_fail' },
      { label: 'BATTERIJ', to: 'print_fail' },
    ],
  },
  /* Printen mislukt in de opname op een lege accu. Dat is geen storing in de
     emulator maar precies wat het apparaat toen deed — en het is nou juist het
     scherm waarover gebeld wordt, dus het hoort erin. */
  print_fail: { type: 'wait', title: 'PARAMETERS', lines: ['PRINTEN'], next: 'print_fail2', ms: 1100 },
  print_fail2: {
    type: 'msg',
    title: 'PARAMETERS',
    lines: ['PRINTEN NIET MOGELIJK', 'ACCUSPANNING TE LAAG', 'CONTROLEER LADER EN', 'TOETS OK'],
  },

  mgr_fun: { type: 'menu', title: 'FUNCTIES', items: [{ label: 'TERMINAL', to: 'mgr_fun_term' }] },
  mgr_fun_term: { type: 'menu', title: 'TERMINAL', items: functieRegels('tms_wait') },
  act_term: { type: 'msg', title: 'ACTIVEER TERMINAL', lines: ['TERMINAL IS AL', 'GEACTIVEERD'] },
  dagopening: {
    type: 'wait', title: 'DAGOPENING', lines: ['EVEN GEDULD AUB'], next: 'dagopening2', ms: 1400,
  },
  dagopening2: { type: 'msg', title: 'DAGOPENING', lines: ['NIET VERBONDEN', 'MET NETWERK'] },
  tms_wait: {
    type: 'wait', title: 'CONTACT TMS', lines: ['VERBINDEN MET TMS', 'EVEN GEDULD AUB'],
    next: 'tms_fail', ms: 1600,
  },
  tms_fail: { type: 'msg', title: 'CONTACT TMS', lines: ['GEEN VERBINDING', 'CONTROLEER NETWERK'] },
  tms_nacht: { type: 'msg', title: 'CONT. TMS VANNACHT', lines: ['TMS-SESSIE', 'INGEPLAND'] },
  logging: {
    type: 'wait', title: 'VERSTUUR LOGGING', lines: ['LOGGING VERSTUREN', 'EVEN GEDULD AUB'],
    next: 'logging2', ms: 1500,
  },
  logging2: { type: 'msg', title: 'VERSTUUR LOGGING', lines: ['GEEN VERBINDING'] },
  reboot: {
    type: 'wait', title: 'OPNIEUW STARTEN', lines: ['OPNIEUW STARTEN', 'EVEN GEDULD AUB'],
    next: 'boot', ms: 1600,
  },
  shutdown: { type: 'msg', title: 'UITZETTEN', lines: ['TERMINAL WORDT', 'UITGEZET'] },
  /* root: na opstarten is de stapel leeg. Zonder dat kon je vanaf het
     startscherm "terug" naar het menu waar je vóór de herstart stond. */
  boot: { type: 'wait', title: '', lines: ['OPSTARTEN'], next: 'start', ms: 1800, root: true },

  mgr_cfg: { type: 'menu', title: 'CONFIGUREREN', items: [{ label: 'TERMINAL', to: 'mgr_cfg_term' }] },
  mgr_cfg_term: {
    type: 'menu',
    title: 'TERMINAL',
    items: [
      { label: 'COMMUNICATIE', to: 'comm' },
      { label: 'MANAGER WACHTWOORD', to: 'cfg_mgrpw' },
      { label: 'MEDEWERKERS', to: 'cfg_medew' },
      { label: 'KEYPAD BL UIT', to: 'cfg_keypad' },
      { label: 'SCHERM HELDERHEID', to: 'cfg_helder' },
      { label: 'TAAL', to: 'cfg_taal' },
    ],
  },
  cfg_mgrpw: { type: 'msg', title: 'MANAGER WACHTWOORD', lines: ['NIEUW WACHTWOORD', 'INVOEREN'] },
  cfg_medew: { type: 'msg', title: 'MEDEWERKERS', lines: ['GEEN MEDEWERKERS', 'GECONFIGUREERD'] },
  cfg_keypad: { type: 'msg', title: 'KEYPAD BL UIT', lines: ['KEYPAD VERLICHTING', 'UITGESCHAKELD'] },
  cfg_helder: { type: 'msg', title: 'SCHERM HELDERHEID', lines: ['HELDERHEID', 'INSTELLEN'] },
  cfg_taal: { type: 'msg', title: 'TAAL', lines: ['NEDERLANDS'] },
  mgr_diag: { type: 'msg', title: 'DIAGNOSE', lines: ['NIET VASTGELEGD', 'IN DE OPNAME'], todo: true },

  /* ── COMMUNICATIE / WIFI ─────────────────────────────────────────────── */
  comm: {
    type: 'menu',
    title: 'COMMUNICATIE',
    items: [
      { label: 'WIFI', sub: wifiSub, to: 'wifi' },
      { label: 'MOBIELE NETWERKEN', sub: 'GEEN VERBINDING', to: 'mobiel' },
      { label: 'USB GADGET', sub: 'SERIAL', to: 'di_usb' },
    ],
  },
  mobiel: {
    type: 'menu',
    title: 'MOBIELE NETWERKEN',
    items: [
      { label: 'IP INFO', sub: 'GEEN VERBINDING', to: 'mob_ip' },
      { label: 'MOBIELE NETW. INFO', sub: 'GEEN VERBINDING', to: 'mob_info' },
      { label: 'HW INFO', sub: 'IMEI N/A', to: 'mob_hw' },
    ],
  },
  mob_ip: { type: 'msg', title: 'IP INFO', lines: ['GEEN VERBINDING'] },
  mob_info: { type: 'msg', title: 'MOBIELE NETW. INFO', lines: ['GEEN VERBINDING'] },
  mob_hw: { type: 'info', title: 'HW INFO', rows: [['IMEI:', 'N/A']] },

  /* Het enige menu dat van vorm verandert: met de wifi uit is er precies één
     regel. Dat is geen vereenvoudiging maar wat het apparaat doet — en het is
     de reden dat mensen bellen ("ik zie die opties niet"). */
  wifi: {
    type: 'menu',
    title: 'WIFI',
    /* Géén returntype erbij schrijven. `: readonly MenuRegel[]` lijkt netter
       maar verbreedt `to` naar string, en dan controleert niets hieronder nog
       of die schermen bestaan — precies de fout die dit bestand moet vangen.
       De `as const` per tak houdt de ids letterlijk. */
    items: (s: ApparaatStaat) =>
      s.wifiAan
        ? ([
            { label: 'WIFI UITZETTEN', to: 'wifi_off' },
            { label: 'HERSTEL VERBINDING', to: 'wifi_herstel' },
            { label: 'WIFI STATUS', to: 'wifi_status' },
            { label: 'ZOEK NETWERKEN', to: 'wifi_scan' },
            { label: 'BEKENDE NETWERKEN', to: 'wifi_bekend' },
            { label: 'HANDMATIG TOEVOEGEN', to: 'wifi_hand' },
            { label: 'IP INFO', to: 'wifi_ipinfo' },
            { label: 'IP INSTELLINGEN', sub: ipSub, to: 'ipadressen' },
          ] as const)
        : ([{ label: 'WIFI AANZETTEN', to: 'wifi_on' }] as const),
  },
  /* pop: 2 — het wachtscherm én het wifimenu waar je vandaan kwam gaan van de
     stapel, zodat `next` het wifimenu OPNIEUW opbouwt. Anders sta je met de
     wifi aan naar de oude lijst van één regel te kijken. */
  wifi_on: {
    type: 'wait', title: 'WIFI', lines: ['WIFI WORDT AANGEZET', 'EVEN GEDULD AUB'], ms: 2200,
    next: 'wifi', pop: 2,
    onDone: (s: ApparaatStaat): ApparaatStaat => ({ ...s, wifiAan: true }),
  },
  wifi_off: {
    type: 'wait', title: 'WIFI', lines: ['WIFI WORDT UITGEZET', 'EVEN GEDULD AUB'], ms: 1400,
    next: 'wifi', pop: 2,
    onDone: (s: ApparaatStaat): ApparaatStaat => ({ ...s, wifiAan: false, wifiVerbonden: false }),
  },
  wifi_herstel: {
    type: 'wait', title: 'WIFI', lines: ['HERSTEL VERBINDING', 'EVEN GEDULD AUB'],
    next: 'wifi_status', ms: 1800,
  },
  wifi_status: { type: 'msg', title: 'WIFI STATUS', lines: wifiLines },
  wifi_scan: {
    type: 'wait', title: 'WIFI', lines: ['ZOEK NETWERKEN', 'EVEN GEDULD AUB'],
    next: 'wifi_netw', ms: 2400,
  },
  wifi_netw: {
    type: 'menu',
    title: 'ZOEK NETWERKEN',
    items: [
      { label: 'Kassa-WiFi', sub: 'WPA2', raw: true, to: 'wifi_connect' },
      { label: 'Kantoor-5G', sub: 'WPA2', raw: true, to: 'wifi_connect' },
      { label: 'Magazijn-2.4G', sub: 'WPA2', raw: true, to: 'wifi_connect' },
      { label: 'Gast-netwerk', sub: 'WPA2', raw: true, to: 'wifi_connect' },
      { label: 'DIRECT-5E-printer', sub: 'WPA2', raw: true, to: 'wifi_connect' },
    ],
  },
  wifi_connect: {
    type: 'wait', title: 'WIFI', lines: ['VERBINDEN', 'EVEN GEDULD AUB'], next: 'wifi_status', ms: 1800,
    onDone: (s: ApparaatStaat): ApparaatStaat => ({ ...s, wifiVerbonden: true }),
  },
  wifi_bekend: { type: 'msg', title: 'BEKENDE NETWERKEN', lines: ['GEEN BEKENDE', 'NETWERKEN'] },
  wifi_hand: { type: 'msg', title: 'HANDMATIG TOEVOEGEN', lines: ['SSID INVOEREN'] },
  wifi_ipinfo: {
    type: 'info',
    title: 'IP INFO',
    rows: [['IP:', '192.168.1.42'], ['MASK:', '255.255.255.0'], ['GW:', '192.168.1.1']],
  },

  ipadressen: {
    type: 'menu',
    title: 'IP ADRESSEN',
    items: [
      { label: 'WIJZIG NAAR DHCP', sub: ipSub, to: 'ip_dhcp' },
      { label: 'TERMINAL IP ADRES', to: 'ip_term' },
      { label: 'SUBNET MASK', to: 'ip_mask' },
      { label: 'GATEWAY', to: 'ip_gw' },
      { label: 'IP ADRES DNS1', to: 'ip_dns1' },
      { label: 'IP ADRES DNS2', to: 'ip_dns2' },
    ],
  },
  ip_dhcp: {
    type: 'wait', title: 'IP ADRESSEN', lines: ['IP MODE', 'DYNAMISCH IP'], next: 'ip_modem', ms: 1500,
    onDone: (s: ApparaatStaat): ApparaatStaat => ({ ...s, ipModus: 'DYNAMISCH IP' }),
  },
  ip_modem: {
    type: 'wait', title: 'OPNIEUW STARTEN', lines: ['SELECTEER MODEM', 'EVEN GEDULD AUB'],
    next: 'boot', ms: 1800,
  },
  ip_term: { type: 'info', title: 'TERMINAL IP ADRES', rows: [['IP:', '192.168.1.42']] },
  ip_mask: { type: 'info', title: 'SUBNET MASK', rows: [['MASK:', '255.255.255.0']] },
  ip_gw: { type: 'info', title: 'GATEWAY', rows: [['GW:', '192.168.1.1']] },
  ip_dns1: { type: 'info', title: 'IP ADRES DNS1', rows: [['DNS1:', '8.8.8.8']] },
  ip_dns2: { type: 'info', title: 'IP ADRES DNS2', rows: [['DNS2:', '8.8.4.4']] },

  /* ── SERVICE MENU ────────────────────────────────────────────────────── */
  servicemenu: {
    type: 'menu',
    title: 'SERVICE MENU',
    items: [
      { label: 'RAPPORTEN', to: 'svc_rap' },
      { label: 'FUNCTIES', to: 'svc_fun' },
      { label: 'CONFIGUREREN', to: 'svc_cfg' },
      { label: 'DIAGNOSE', to: 'svc_diag' },
      { label: 'MANAGER MENU', to: 'managermenu' },
    ],
  },

  svc_rap: { type: 'menu', title: 'RAPPORTEN', items: [{ label: 'TERMINAL', to: 'svc_rap_term' }] },
  svc_rap_term: {
    type: 'menu',
    title: 'TERMINAL',
    items: [
      { label: 'PARAMETERS', to: 'print_fail' },
      { label: 'MANAGEMENT INFO', to: 'print_fail' },
      { label: 'KEY', to: 'print_fail' },
      { label: 'TMS SESSIES', to: 'print_fail' },
      { label: 'LAATSTE FOUT BER.', to: 'print_fail' },
      { label: 'CRASH RAPPORT', to: 'print_fail' },
    ],
  },

  svc_fun: { type: 'menu', title: 'FUNCTIES', items: [{ label: 'TERMINAL', to: 'svc_fun_term' }] },
  svc_fun_term: { type: 'menu', title: 'TERMINAL', items: functieRegels('svc_tms') },
  svc_tms: {
    type: 'menu',
    title: 'CONTACT TMS',
    items: [
      { label: 'NORMAAL', to: 'tms_wait' },
      { label: 'NOODOPROEP', to: 'tms_wait' },
      { label: 'HW INSTALLATIE', to: 'tms_wait' },
    ],
  },

  svc_cfg: { type: 'menu', title: 'CONFIGUREREN', items: [{ label: 'TERMINAL', to: 'svc_cfg_term' }] },
  svc_cfg_term: {
    type: 'menu',
    title: 'TERMINAL',
    items: [
      { label: 'COMMUNICATIE', to: 'comm' },
      { label: 'DATUM / TIJD', to: 'svc_datum' },
      { label: 'ILS2COM', to: 'svc_ils' },
      { label: 'KEYPAD BL UIT', to: 'cfg_keypad' },
      { label: 'SCHERM KALIBREREN', to: 'svc_kalib' },
      { label: 'RESET MANAGER WW', to: 'svc_resetmgr' },
      { label: 'RESET TRANSACTIE WW', to: 'svc_resettrx' },
      { label: 'LADEN PP KEYS', to: 'svc_ppkeys' },
    ],
  },
  svc_datum: {
    type: 'info', title: 'DATUM / TIJD', rows: [['DATUM:', '11/09/2026'], ['TIJD:', '12:12:40']],
  },
  svc_ils: { type: 'msg', title: 'ILS2COM', lines: ['ILS2COM', 'INSTELLINGEN'] },
  svc_kalib: { type: 'msg', title: 'SCHERM KALIBREREN', lines: ['RAAK DE KRUISJES', 'AAN'] },
  svc_resetmgr: { type: 'msg', title: 'RESET MANAGER WW', lines: ['MANAGER WACHTWOORD', 'GERESET'] },
  svc_resettrx: { type: 'msg', title: 'RESET TRANSACTIE WW', lines: ['TRANSACTIE WACHTWOORD', 'GERESET'] },
  svc_ppkeys: {
    type: 'wait', title: 'LADEN PP KEYS', lines: ['LADEN PP KEYS', 'EVEN GEDULD AUB'],
    next: 'svc_ppkeys2', ms: 1800,
  },
  svc_ppkeys2: { type: 'msg', title: 'LADEN PP KEYS', lines: ['GEEN VERBINDING'] },
  svc_diag: { type: 'msg', title: 'DIAGNOSE', lines: ['NIET VASTGELEGD', 'IN DE OPNAME'], todo: true },
} as const satisfies Record<string, Scherm>;

/** Afgeleid, niet onderhouden: elke sleutel hierboven is automatisch een id. */
export type SchermId = keyof typeof RUW;

/**
 * DE CONTROLE. Deze ene toekenning eist dat élke `to` en élke `next` hierboven
 * een bestaand scherm noemt. Een typefout geeft hier een compilerfout mét het
 * pad naar het scherm waarin hij staat — in plaats van een knop die in de
 * browser niets doet.
 *
 * Dat het Record over SchermId gaat en niet over string is de tweede winst:
 * onder noUncheckedIndexedAccess levert SCHERMEN[id] een Scherm op en geen
 * `Scherm | undefined`, dus nergens in de componenten een terugvalscherm.
 */
export const SCHERMEN: Readonly<Record<SchermId, Scherm<SchermId>>> = RUW;

/** Waar de menuboom begint. Het opstartscherm staat daar bewust buiten: dat
 *  is geen menu-item maar de doorgang ernaartoe. */
export const WORTEL = 'main' satisfies SchermId;
export const SPLASH = 'start' satisfies SchermId;

export const isSchermId = (v: string): v is SchermId => Object.hasOwn(SCHERMEN, v);

export const BEGINSTAAT: ApparaatStaat = {
  wifiAan: false,
  wifiVerbonden: false,
  ipModus: 'STATISCH IP',
};

export const V400M: Toestel = {
  slug: 'v400m',
  aliassen: ['p400', 'v400'],
  naam: 'Verifone V400M & P400',
  kort: 'Verifone',
  merk: 'CCV-app · 15.0.07',
  omschrijving:
    'De klassieke CCV-app: een blauwe balk met vijf regels per scherm en bladerpijlen. Dezelfde menu’s als op een PAX, maar een heel ander beeld om naar te wijzen.',
  chrome: 'verifone',
  schermen: SCHERMEN,
  wortel: WORTEL,
  splash: SPLASH,
  regelsPerScherm: 5,
  beginstaat: BEGINSTAAT,
  snel: [
    { label: 'Wifi instellen', scherm: 'wifi' },
    { label: 'Contact TMS', scherm: 'svc_tms' },
    { label: 'Papier doorvoeren', scherm: 'paperfeed' },
  ],
  bron:
    'Menustructuur en schermteksten komen uit een opname van een Verifone V400M met de CCV-app (softwareversie 15.0.07). De P400 draait dezelfde app. Schermen die in die opname niet geopend zijn, staan als zodanig gemarkeerd. Serienummer, IP-adressen en netwerknamen zijn verzonnen voorbeelden.',
  serviceScherm: 'servicemenu',
  managerScherm: 'managermenu',
  managerCode: '99999',
};
